import * as Yup from "yup";
import { useCallback, useEffect, useState } from "react";
import { useBoolean } from "../../../hooks/use-boolean";
import { IDepartment, IDepartmentCreate, IDepartmentFilters } from "../../../types/department";
import { IPagination } from "../../../types/pagination";
import { defaultFilters, departmentOrderOptions } from "../../../constants/department";
import { isEqual } from "lodash";
import { IBranch } from "../../../types/branch";
import useTable from "../../../hooks/use-table";
import { AllBranchesService } from "../../../services/branches";
import { enqueueSnackbar } from "notistack";
import { ActiveDepartmentService, CreateDepartmentService, DeleteDepartmentService, DepartmentsService } from "../../../services/departments";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IUser } from "../../../types/user";
import { AllUsersService } from "../../../services/users";

const useDepartmentList = () => {

    // hooks
    const openFilters = useBoolean();
    const loading = useBoolean(true);
    const loadingFilters = useBoolean(true);
    const openCreateDialog = useBoolean();
    const table = useTable({
        defaultDirection: departmentOrderOptions[0].direction,
        defaultSortBy: departmentOrderOptions[0].sortBy
    });

    // states
    const [filters, setFilters] = useState<IDepartmentFilters>(defaultFilters);
    const [branches, setBranches] = useState<IBranch[]>([]);
    const [departmentsList, setDepartmentsList] = useState<IPagination<IDepartment> | null>(null);
    const [users, setUsers] = useState<IUser[]>([]);

    // rhf
    const NewDepartmentSchema = Yup.object().shape({
        name: Yup.string().required('Informe o nome do departamento').max(255, 'O nome deve conter no máximo 255 caracteres'),
        branch: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            code: Yup.string().required(),            
        }).required("Selecione uma filial"),
        manager: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            email: Yup.string().required(),
        }).required("Selecione o gestor(a)")
    });    

    const methods = useForm({
        resolver: yupResolver<IDepartmentCreate>(NewDepartmentSchema),
        defaultValues: {
            name: '',
            branch: null!,
            manager: null!
        }
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
        reset,
    } = methods;

    // functions
    const canReset = !isEqual(defaultFilters, filters);

    const handleFilters = useCallback((name: string, value: string | null | IBranch[]) => {
        setFilters((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, []);

    const getData = async () => {

        loadingFilters.onTrue();

        await AllBranchesService().then(resp => {
            setBranches(resp.data);
        }).catch(error => {
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });

        await AllUsersService().then(resp => {
            setUsers(resp.data);
        }).catch(error => {
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });

        loadingFilters.onFalse();

    }

    const getDepartments = async () => {

        loading.onTrue();

        await DepartmentsService(table, filters).then(resp => {
            setDepartmentsList(resp.data);
        }).catch(error => {
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });

        loading.onFalse();

    }

    const onSubmit = handleSubmit(async (data: IDepartmentCreate) => {

        loading.onTrue();

        await CreateDepartmentService(data).then(resp => {
            enqueueSnackbar(resp.data.message);
            reset({
                name: '',
                branch: null!,
                manager: null!
            });
            openCreateDialog.onFalse();
            getDepartments();
        }).catch(error => {
            loading.onFalse();
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });

    });

    const deleteDepartment = async (id: number) => {

        loading.onTrue();

        await DeleteDepartmentService(id).then(resp => {
            enqueueSnackbar(resp.data.message);
            getDepartments();
        }).catch(error => {
            loading.onFalse();
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });

    }

    const activeDepartment = async (id: number) => {

        loading.onTrue();

        await ActiveDepartmentService(id).then(resp => {
            enqueueSnackbar(resp.data.message);
            getDepartments();
        }).catch(error => {
            loading.onFalse();
            enqueueSnackbar(error?.response?.data?.message || 'Ocorreu um erro', {
                variant: 'error'
            });
        });

    }

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        getDepartments();
    }, [table.page, table.pageSize, table.direction, table.sortBy])

    return {
        openFilters,
        filters,
        canReset,
        handleFilters,
        resetFilters,
        table,
        loading,
        loadingFilters,
        getDepartments,
        departmentsList,
        branches,
        openCreateDialog,
        methods,
        isSubmitting,
        onSubmit,
        deleteDepartment,
        activeDepartment,
        users
    }
}

export default useDepartmentList;